import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ModalService, ModalSettings, WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { QuickRestoreSchemaModalComponent } from 'mbs-ui-shared@components/quick-restore-schema-modal/quick-restore-schema-modal.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { Administrator, PermissionsEnum, TempTokenData } from 'mbs-ui-shared@models';
import { GetPlanTypeFromBunchType } from 'mbs-ui-shared@models/backup/storages-type';
import { Build } from 'mbs-ui-shared@models/build';
import Computer, { OsType } from 'mbs-ui-shared@models/Computer';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { getPointInTime } from 'mbs-ui-shared@utils/date';
import { isWindows } from 'mbs-ui-shared@utils/is-windows';
import { RestoreWizardWrapper } from 'mbs-ui-shared@wrappers/restoreWizard.wrapper';
import { first, noop, of } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { RestoreWizardSettings } from 'wizards/models/base/base-plan-models';
import {
  FromToStepData,
  GenericRestoreData,
  RestoreToVariants,
  WhatToStepData
} from 'wizards/models/generic-restore/generic-restore-models';
import { RestoreType } from 'wizards/models/restore-point-models';
import { SimpleWizardBaseClass } from 'wizards/remote-management-wizards/simple-wizard-base.class';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { ParamsForInitForeignBrowsingSession } from 'wizards/services/wizard-steps.service';
import { GenericRestoreStepsModule } from 'wizards/steps/only-restore-steps/generic-restore-steps/generic-restore-steps-module';

type GenericRestoreWizardHelpDocumentationLinks =
  | 'selectFromTo'
  | 'selectPoint'
  | 'whatToRestore'
  | 'destination'
  | 'notification'
  | 'summary';

@UntilDestroy()
@Component({
  selector: 'mbs-generic-restore-wizard',
  templateUrl: './generic-restore-wizard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, GenericRestoreStepsModule, I18NextPipe, WizardStepComponent, WizardComponent]
})
export class GenericRestoreWizardComponent extends SimpleWizardBaseClass {
  public ability = inject(AbilityService);
  private auth = inject(AuthFacade);
  private buildFacade = inject(BuildsFacade);
  private modalService = inject(ModalService);
  private restoreWizardWrapper = inject(RestoreWizardWrapper);

  public readonly title = this.i18nPipe.transform('wizards:generic.title');
  private readonly quickRestoreModalSettings: ModalSettings = {
    footer: {
      okButton: { show: false },
      cancelButton: { text: this.i18nPipe.transform('shared:buttons.close', { format: 'title' }) }
    }
  };

  public wizardFinish: WritableSignal<boolean> = signal(false);
  public loadingPlan: WritableSignal<boolean> = signal(false);

  public helpLink: WizardHelpLink<GenericRestoreWizardHelpDocumentationLinks> = new WizardHelpLink(this.helpService, this);

  public ownerId = '';
  public data?: { computer?: Computer };

  public FormModel: GenericRestoreData = {
    fromToStepData: null,
    whatToStepData: null
  };

  get wizardTitle() {
    const fromToData = this.FormModel?.fromToStepData;

    if (!fromToData?.fromComputer) return this.title;

    if (fromToData.restoreTo === RestoreToVariants.current) {
      return this.i18nPipe.transform('wizards:generic.fromToCurrentTitle', { name: fromToData.fromComputer?.name });
    }

    if (fromToData.restoreTo === RestoreToVariants.original) {
      return this.i18nPipe.transform('wizards:generic.fromToOriginalTitle', { name: fromToData.fromComputer?.name });
    }

    return this.i18nPipe.transform('wizards:generic.fromToTitle', {
      firstName: fromToData.fromComputer?.name,
      secondName: fromToData.toComputer?.name
    });
  }

  constructor() {
    super();

    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user: Administrator) => (this.ownerId = user?.ProviderInfo?.Id || ''));
  }

  fromToStepDataChangeHandler(fromToStepData: FromToStepData): void {
    this.FormModel.fromToStepData = fromToStepData;
  }

  whatToStepDataChangeHandler(whatToStepData: WhatToStepData): void {
    this.FormModel.whatToStepData = whatToStepData;
  }

  handleSave(): void {
    if (this.FormModel.fromToStepData?.restoreTo === RestoreToVariants.current) {
      return this.restoreToCurrentAndCloseWizard();
    }

    this.loadingPlan.set(true);
    this.restoreToOriginalAndCloseWizard(this.FormModel.fromToStepData?.restoreTo === RestoreToVariants.original);
  }

  private restoreToCurrentAndCloseWizard(): void {
    this.buildFacade.builds$.pipe(filter(Boolean), take(1), untilDestroyed(this)).subscribe((builds: Build[]) => {
      const downloadLinkData: Build = (builds ?? []).find((build) => build.buildType === 'QuickRestore');
      const modal = this.modalService.openRef(QuickRestoreSchemaModalComponent, this.quickRestoreModalSettings);

      modal.componentInstance.ignoreIsWindows = true;
      modal.componentInstance.needPreparingTextShow = true;
      modal.componentInstance.downloadLink = downloadLinkData?.public?.downloadLink;

      modal.result.then(noop).catch(noop);

      if (
        (this.ability.can('read', PermissionsEnum.RemoteManagement) || !this.auth.isMBSMode) &&
        this.ability.can('read', PermissionsEnum.QuickRestore)
      ) {
        queueMicrotask(() => this.createQuickRestoreSchema(modal));
      }
    });
  }

  private restoreToOriginalAndCloseWizard(original = true): void {
    const computer: Computer = this.FormModel.fromToStepData.fromComputer;

    if (original || computer?.online) this.modalService.dismissAll();

    const isWindows = computer?.os === OsType.windows;
    const plan = this.FormModel.whatToStepData.plan;
    const pointData = this.FormModel.whatToStepData.point;
    const pathArray = (pointData?.path || plan.path)?.split(isWindows ? '\\' : '/');
    const type = GetPlanTypeFromBunchType[plan.type] || PlanTypes.RestorePlan;
    const pointStr =
      this.FormModel.whatToStepData.pointType === RestoreType.PointInTime
        ? isWindows
          ? pathArray?.[pathArray.length - 1]
          : pointData.restorePointId
        : null;
    const point = getPointInTime(pointStr, plan.isNBF);
    const password = this.FormModel.whatToStepData?.password;

    const initialFormData: { [key: string]: unknown } = {
      whereBackupStep: { ConnectionID: this.FormModel?.whatToStepData?.destination?.ID },
      computerToRestoreStep: { computerName: pathArray?.[1] || '' },
      backupToRestoreStep: { backupPlanName: plan.bunchName },
      restorePointStep: {
        restoreType: point?.date && point.time ? RestoreType.PointInTime : RestoreType.LatestVersion,
        fromDateTime: point
      }
    };

    if (password) initialFormData.encryptionOptionsStep = { password };

    const settings: RestoreWizardSettings = { computer, isNBF: plan.isNBF, type, initialFormData };

    const toComputer = this.FormModel.fromToStepData.toComputer;

    if (!original) {
      settings.targetComputerData = {
        hid: toComputer.hid,
        computerName: toComputer.name,
        userId: toComputer.userAccount?.id,
        email: toComputer.userAccount?.accountName
      };
    }

    if (computer?.online || original) {
      this.loadingPlan.set(false);

      return void setTimeout(() => this.restoreWizardWrapper.openRestoreWizard$.next(settings));
    }

    settings.computer = toComputer;
    this.openWizardForOfflineSourceComputer(settings, computer, toComputer);
  }

  private openWizardForOfflineSourceComputer(settings: RestoreWizardSettings, from: Computer, to: Computer): void {
    const params: ParamsForInitForeignBrowsingSession = {
      agentType: 'backup',
      commandType: 'InitForeignBrowsingSession',
      params: { UserId: from.userAccount?.id }
    };

    this.stepService
      .getRemoteCommandData(params, to.hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: { data: { sessionId: string } }) => {
          settings.sessionId = result?.data?.sessionId;
          settings.sourceHid = from.hid;
          settings.sourceUserId = from.userAccount?.id;
          setTimeout(() => this.restoreWizardWrapper.openRestoreWizard$.next(settings));
          this.modalService.dismissAll();
          this.loadingPlan.set(false);
        },
        error: () => {
          this.wizard.spinnerShow = false;
          this.loadingPlan.set(false);
        }
      });
  }

  private createQuickRestoreSchema(modal: NgbModalRef): void {
    modal.componentInstance.preparing = true;
    const computer: Computer = this.FormModel.fromToStepData.fromComputer;
    const whatToStep: WhatToStepData = this.FormModel.whatToStepData;
    const isWindows = computer?.os === OsType.windows;

    this.auth
      .tempToken$(computer.userAccount.id, computer.hid, 'OneTimeQuickRestore')
      .pipe(
        catchError(() => of(null)),
        untilDestroyed(this)
      )
      .pipe(
        map((tokenData: TempTokenData) => ({
          UserToken: tokenData?.token,
          RestorePointObjectPath: this.getRestorePointObjectPath(whatToStep, isWindows),
          ownerId: this.ownerId
        })),
        untilDestroyed(this)
      )
      .subscribe({
        next: (jsonIbj: { [key: string]: string }) => {
          modal.componentInstance.preparing = false;
          modal.componentInstance.createSchemaParams = jsonIbj;
          this.wizardFinish.set(true);
        },
        error: () => {
          modal.componentInstance.preparing = false;
          this.wizardFinish.set(true);
        }
      });
  }

  private getRestorePointObjectPath(whatToStep: WhatToStepData, isWindowsComp: boolean): string {
    if (whatToStep.pointType !== RestoreType.PointInTime) {
      return isWindows() && whatToStep.plan.path.includes('/') ? whatToStep.plan.path.replaceAll('/', '\\') : whatToStep.plan.path;
    }

    const slash = isWindowsComp ? '\\' : '/';
    const path =
      isWindowsComp && whatToStep.point?.path ? whatToStep.point?.path : whatToStep.plan.path + slash + whatToStep.point?.restorePointId;

    return isWindows() && path.includes('/') ? path.replaceAll('/', '\\') : path;
  }
}
